/**
 * Naming the error (BRIEF <spanish_correctness> layer 2, SPEC §2 `lang/nearmiss.ts`).
 *
 * > Near-miss classification so feedback names the error type — wrong gender, wrong person,
 * > missing accent, ser/estar confusion — rather than just marking it wrong.
 *
 * The whole point is the *name*. A learner who is told "wrong" learns that they were wrong; one
 * told "that is the `tú` form, and this slot wants `él`" learns Spanish. So this module returns a
 * kind, and `null` means correct — never a bare boolean.
 *
 * Order matters. The checks run cheapest-and-most-specific first, because an answer can satisfy
 * more than one description and the most specific one is the useful feedback: `está` for `es` is a
 * ser/estar confusion, not "wrong person", even though it is also a different person.
 *
 * Pure.
 */

import { compareAccents, matchesExactly, matchesIgnoringAccents, stripAccents } from './accents';
import { PERSONS, TENSES, conjugate, type Person, type Tense } from './conjugation';
import { checkPhrase, type Phrase } from './agreement';
import type { NearMissKind } from '../store/schema';

export type { NearMissKind };

/** What the item knows about the answer it wants. All optional: a bare word has none of it. */
export interface ClassifyContext {
  /** A conjugation slot: which verb, which tense, which person the item is asking for. */
  verb?: { infinitive: string; tense: Tense; person: Person };
  /** A noun phrase, with the gender and number it should carry. */
  phrase?: Phrase;
}

/** `null` means the answer is right. Every other value is a name the feedback card can use. */
export function classify(
  expected: string,
  actual: string,
  context: ClassifyContext = {},
): NearMissKind | null {
  const wanted = expected.trim();
  const given = actual.trim();

  if (given.length === 0) return 'unrelated';
  if (matchesExactly(wanted, given)) return null;

  // 1. Accents, before anything else: the letters are identical, so no other check can be more
  //    specific than this one.
  if (matchesIgnoringAccents(wanted, given)) {
    const comparison = compareAccents(wanted, given);
    if (comparison === 'missing-accent' || comparison === 'wrong-accent') return comparison;
    /*
     * Same letters, same accents, and `matchesExactly` still said no — so the difference is
     * punctuation or spacing, and there is nothing here to tell the learner. Returning here rather
     * than falling through matters: the typo check further down would call a missing comma a
     * spelling mistake. Scoring has ignored punctuation since T29; this is the classifier agreeing
     * with it (D-176).
     */
    return null;
  }

  // 2. ser/estar, before the person and tense checks. An answer that is the right idea in the
  //    wrong verb is a ser/estar confusion first and a conjugation slip only incidentally, and
  //    telling the learner "wrong person" here would send them to fix the wrong thing.
  if (isSerEstarConfusion(wanted, given)) return 'ser-estar';

  // 3. The conjugation slot.
  if (context.verb) {
    const slip = classifyVerbSlot(context.verb, given);
    if (slip) return slip;
  }

  // 4. The noun phrase.
  if (context.phrase) {
    const result = checkPhrase(context.phrase, given);
    if (result.error === 'gender') return 'wrong-gender';
    if (result.error === 'number') return 'wrong-number';
    if (result.error === 'article') return 'wrong-gender';
  }

  // 5. A near-identical string is a typo, not a grammar error.
  if (looksLikeTypo(wanted, given)) return 'spelling';

  return 'unrelated';
}

// ---------------------------------------------------------------------------------------------
// ser / estar
// ---------------------------------------------------------------------------------------------

function formsOf(infinitive: string): Set<string> {
  const out = new Set<string>();
  for (const tense of TENSES) {
    for (const person of PERSONS) {
      out.add(stripAccents(conjugate(infinitive, tense, person)).toLowerCase());
    }
  }
  return out;
}

const SER_FORMS = formsOf('ser');
const ESTAR_FORMS = formsOf('estar');

/**
 * The single most-diagnosed error in Spanish learning, and the one worth catching by name.
 *
 * Only fires when the answer is a form of the *other* verb — not merely wrong. `fui` is a form of
 * both `ser` and `ir`, so an answer shared between the two verbs is not evidence of confusion and
 * is deliberately excluded.
 */
export function isSerEstarConfusion(expected: string, actual: string): boolean {
  const wanted = stripAccents(expected.trim()).toLowerCase();
  const given = stripAccents(actual.trim()).toLowerCase();

  const wantedSer = SER_FORMS.has(wanted) && !ESTAR_FORMS.has(wanted);
  const wantedEstar = ESTAR_FORMS.has(wanted) && !SER_FORMS.has(wanted);
  const givenSer = SER_FORMS.has(given) && !ESTAR_FORMS.has(given);
  const givenEstar = ESTAR_FORMS.has(given) && !SER_FORMS.has(given);

  return (wantedSer && givenEstar) || (wantedEstar && givenSer);
}

// ---------------------------------------------------------------------------------------------
// Conjugation slots
// ---------------------------------------------------------------------------------------------

/**
 * Which slot the learner actually produced.
 *
 * A form can fill several slots at once (`hablamos` is present and preterite), so this looks at
 * every match and prefers the reading that keeps one axis right: an answer that is the correct
 * tense in the wrong person is reported as a person error, because that is the smaller correction
 * and the one the learner can act on.
 */
function classifyVerbSlot(
  slot: { infinitive: string; tense: Tense; person: Person },
  given: string,
): NearMissKind | null {
  const wanted = stripAccents(given).toLowerCase();
  const matches: Array<{ tense: Tense; person: Person }> = [];
  for (const tense of TENSES) {
    for (const person of PERSONS) {
      if (stripAccents(conjugate(slot.infinitive, tense, person)).toLowerCase() === wanted) {
        matches.push({ tense, person });
      }
    }
  }
  if (matches.length === 0) return null;

  if (matches.some((m) => m.tense === slot.tense)) return 'wrong-person';
  if (matches.some((m) => m.person === slot.person)) return 'wrong-tense';
  // Neither axis survives. The tense is the bigger of the two misses, so it leads.
  return 'wrong-tense';
}

// ---------------------------------------------------------------------------------------------
// Typos
// ---------------------------------------------------------------------------------------------

/** Levenshtein, iterative and allocation-light. Words here are short. */
export function editDistance(a: string, b: string): number {
  const s = stripAccents(a.toLowerCase());
  const t = stripAccents(b.toLowerCase());
  if (s === t) return 0;

  let previous = Array.from({ length: t.length + 1 }, (_, i) => i);
  for (let i = 1; i <= s.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= t.length; j += 1) {
      const substitution = previous[j - 1]! + (s[i - 1] === t[j - 1] ? 0 : 1);
      current[j] = Math.min(substitution, previous[j]! + 1, current[j - 1]! + 1);
    }
    previous = current;
  }
  return previous[t.length]!;
}

/**
 * Close enough to be a slip of the thumb rather than a different word.
 *
 * Scaled to length, because one wrong letter in a three-letter word is a different word and one
 * wrong letter in `refrigerador` is a typo. Usage scenario 1 is one thumb in the dark, so this
 * distinction earns its place.
 */
export function looksLikeTypo(expected: string, actual: string): boolean {
  const distance = editDistance(expected, actual);
  if (distance === 0) return false;
  const length = Math.max(stripAccents(expected).length, stripAccents(actual).length);
  if (length <= 4) return false;
  return distance <= (length >= 9 ? 2 : 1);
}

// ---------------------------------------------------------------------------------------------
// Naming
// ---------------------------------------------------------------------------------------------

/** Feedback text for each kind. Names the error; never scolds, never says only "wrong". */
export const NEAR_MISS_LABELS: Readonly<Record<NearMissKind, string>> = {
  'missing-accent': 'The accent is missing.',
  'wrong-accent': 'The accent is in the wrong place.',
  'wrong-gender': 'Gender does not agree.',
  'wrong-number': 'Singular and plural do not agree.',
  'wrong-person': 'Right tense, wrong person.',
  'wrong-tense': 'Right verb, wrong tense.',
  'ser-estar': 'That is the other verb — ser and estar.',
  spelling: 'One letter out.',
  unrelated: 'Not this one.',
};
